import { Link } from 'react-router-dom';
import { Target, Network, Link2, Gift, Activity, ArrowRight } from 'lucide-react';

const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_protect-play-grow/artifacts/yiepe3o0_AdobeStock_299339591.jpeg";

export const About = () => {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Mission Section */}
      <section className="py-20 md:py-32" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-6">
              Our Mission
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold text-[#272926] leading-tight mb-8">
              We are a catalyst to protect athletes and open doors to the power of sport for all.
            </h1>
            <p className="text-xl font-semibold text-[#5B4FF9]">
              Protecting Kids. Powering Possibilities.
            </p>
          </div>
        </div>
      </section>

      {/* Why We Exist Section */}
      <section className="py-20 md:py-32 bg-zinc-50" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
                Our Purpose
              </p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug mb-8">
                Why this work matters
              </h2>
              <p className="text-lg leading-relaxed text-zinc-600 mb-6">
                Sport has the power to change lives—but only when it is safe and accessible.
              </p>
              <p className="text-lg leading-relaxed text-zinc-600">
                Today, too many young athletes face barriers that limit participation or put them at risk. The Players Health Foundation exists to address both challenges—together.
              </p>
            </div>
            <div className="relative">
              <img 
                src={ABOUT_IMAGE} 
                alt="Volunteer guiding kids in sports"
                className="rounded-3xl shadow-xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#5B4FF9] rounded-2xl -z-10"></div>
              <div className="absolute -top-6 -right-6 w-16 h-16 bg-amber-400 rounded-xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Create Change Section */}
      <section className="py-20 md:py-32" data-testid="change-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              Our Framework
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug">
              How we create change
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            {/* Reclaim the Game - Large */}
            <div className="md:col-span-3 bg-[#5B4FF9] rounded-3xl p-8 text-white" data-testid="block-reclaim">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                <Target className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Reclaim the Game</h3>
              <p className="text-purple-100 leading-relaxed">
                We take meaningful action to create safer, more positive outcomes in youth sports.
              </p>
            </div>

            {/* Network Effect - Large */}
            <div className="md:col-span-3 bg-[#272926] rounded-3xl p-8 text-white" data-testid="block-network">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                <Network className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Network Effect</h3>
              <p className="text-zinc-300 leading-relaxed">
                We unite voices across the sports ecosystem to spark change and expand impact.
              </p>
            </div>

            {/* Connective Tissue - Medium */}
            <div className="md:col-span-2 bg-[#F5F4FF] border border-[#E5E3FF] rounded-3xl p-8" data-testid="block-connective">
              <div className="w-12 h-12 bg-[#E5E3FF] rounded-xl flex items-center justify-center mb-6">
                <Link2 className="h-6 w-6 text-[#5B4FF9]" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Connective Tissue</h3>
              <p className="text-zinc-600 leading-relaxed">
                We bring together organizations, partners, and communities to support every athlete.
              </p>
            </div>

            {/* Play It Forward - Medium */}
            <div className="md:col-span-2 bg-amber-50 border border-amber-100 rounded-3xl p-8" data-testid="block-play">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                <Gift className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Play It Forward</h3>
              <p className="text-zinc-600 leading-relaxed">
                We inspire generosity to fuel opportunity and give more kids the chance to play.
              </p>
            </div>

            {/* Hitting Our Stride - Medium */}
            <div className="md:col-span-2 bg-zinc-100 border border-zinc-200 rounded-3xl p-8" data-testid="block-stride">
              <div className="w-12 h-12 bg-zinc-200 rounded-xl flex items-center justify-center mb-6">
                <Activity className="h-6 w-6 text-zinc-700" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Hitting Our Stride</h3>
              <p className="text-zinc-600 leading-relaxed">
                We build the systems, structure, and accountability needed to deliver lasting impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Relationship to Players Health */}
      <section className="py-20 md:py-32 bg-zinc-50" data-testid="relationship-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              Our Partnership
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug mb-8">
              Built in partnership with Players Health
            </h2>
            <p className="text-lg leading-relaxed text-zinc-600 mb-6">
              The Players Health Foundation works alongside Players Health to advance a shared mission of safer, more accessible sports.
            </p>
            <p className="text-lg leading-relaxed text-zinc-600">
              While closely aligned in purpose, the Foundation operates as an independent nonprofit focused on delivering community impact.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#5B4FF9]" data-testid="about-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white leading-snug mb-8">
              Ready to make a difference?
            </h2>
            <Link
              to="/get-involved"
              className="bg-white text-[#5B4FF9] rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg inline-flex items-center justify-center"
              data-testid="about-get-involved-btn"
            >
              Get Involved
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
